/**
 * HTTP Utility
 *
 * Provides authenticated fetch for API calls.
 * Used by beaconApi service layer.
 */
import {
  getToken,
  isExpired,
  getRefreshToken,
  refreshIdToken,
  buildLoginUrl,
  stashPostLoginRedirect,
} from 'src/auth';

const buildHeaders = (token: string | null, method: 'GET' | 'POST'): Record<string, string> => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method === 'POST') headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

/**
 * Returns a usable id_token. If the stored one is expired (or missing) and a
 * refresh token exists, silently refreshes it first — no page reload.
 */
const getValidToken = async (): Promise<string | null> => {
  const token = getToken();
  if (token && !isExpired(token)) return token;
  if (getRefreshToken()) return refreshIdToken();
  return token;
};

/** Redirect to Cognito to re-authenticate — last resort when the refresh token is also dead. */
const redirectToLogin = (): void => {
  stashPostLoginRedirect();
  buildLoginUrl().then((url) => { window.location.href = url; }).catch(() => {});
};

/**
 * Promise-based fetch with auth — for use with createAsyncThunk.
 * Adds the Bearer id_token, silently refreshing it on expiry (proactively) and on a
 * 401/403 (reactively, then retries once). Only redirects to login if refresh fails.
 * Returns parsed JSON or throws on error.
 */
export const fetchAsync = async <T = any>(url: string, method: 'GET' | 'POST' = 'GET', body?: string): Promise<T> => {
  const token = await getValidToken();
  let res = await fetch(url, { method, headers: buildHeaders(token, method), body });

  // Refresh only on 401 (bad/expired token). 403 is "forbidden" (authenticated but not
  // allowed) — a refresh can't fix that, so let it fall through to avoid a redirect loop.
  if (res.status === 401) {
    const fresh = await refreshIdToken();
    if (fresh) {
      res = await fetch(url, { method, headers: buildHeaders(fresh, method), body });
    }
    if (res.status === 401) {
      redirectToLogin();
      throw new Error('Session expired — redirecting to login.');
    }
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => null);
    throw new Error(errorBody?.message || `API error: ${res.status}`);
  }
  const json = await res.json();
  return json.data ?? json;
};
